/**
 * NIP-59 Gift Wrapping for Cashero.
 *
 * Wraps unsigned rumors inside NIP-44 encrypted seals (kind 13)
 * and gift wraps (kind 1059) to hide metadata on the wire.
 *
 * All wallet↔mint communication uses this layer.
 */

import { nip44, nip19, finalizeEvent, getPublicKey, generateSecretKey, getEventHash } from 'nostr-tools';
import type { EventTemplate, NostrEvent } from 'nostr-tools';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';

/**
 * Create a NIP-44 conversation key from a private key and a recipient's public key.
 * The key is symmetric: conv(a, B) == conv(b, A).
 */
export function getConversationKey(privateKeyHex: string, publicKeyHex: string): string {
  return nip44.v2.utils.getConversationKey(privateKeyHex, publicKeyHex);
}

/**
 * Encrypt a JSON-serializable object using NIP-44 v2.
 */
export function nip44Encrypt(data: unknown, privateKeyHex: string, publicKeyHex: string): string {
  const convKey = getConversationKey(privateKeyHex, publicKeyHex);
  return nip44.v2.encrypt(JSON.stringify(data), convKey);
}

/**
 * Decrypt a NIP-44 v2 encrypted payload.
 */
export function nip44Decrypt<T = unknown>(payload: string, privateKeyHex: string, publicKeyHex: string): T {
  const convKey = getConversationKey(privateKeyHex, publicKeyHex);
  const decrypted = nip44.v2.decrypt(payload, convKey);
  return JSON.parse(decrypted) as T;
}

/**
 * Create a rumor (unsigned event) ready for sealing.
 */
export function createRumor(
  kind: number,
  content: string,
  tags: string[][],
  pubkey: string,
): EventTemplate {
  return {
    kind,
    content,
    tags,
    created_at: Math.floor(Date.now() / 1000),
    pubkey,
  };
}

/**
 * Seal a rumor inside a kind 13 event.
 *
 * The rumor is NIP-44 encrypted between the sender's key and the recipient's key,
 * then placed in a signed kind 13 event.
 *
 * @param rumor - The unsigned rumor to seal
 * @param senderPrivkeyHex - Sender's private key (hex)
 * @param recipientPubkeyHex - Recipient's public key (hex)
 * @returns The signed kind 13 seal event
 */
export function createSeal(
  rumor: EventTemplate,
  senderPrivkeyHex: string,
  recipientPubkeyHex: string,
): NostrEvent {
  const senderPrivkeyBytes = hexToBytes(senderPrivkeyHex);
  const encryptedRumor = nip44Encrypt(rumor, senderPrivkeyHex, recipientPubkeyHex);

  const sealTemplate: EventTemplate = {
    kind: 13,
    content: encryptedRumor,
    tags: [],
    created_at: Math.floor(Date.now() / 1000),
    pubkey: getPublicKey(senderPrivkeyBytes),
  };

  return finalizeEvent(sealTemplate, senderPrivkeyBytes) as NostrEvent;
}

/**
 * Gift wrap a seal inside a kind 1059 event.
 *
 * The seal is NIP-44 encrypted with a random one-time key to the recipient,
 * then signed by that random key. A p-tag identifies the recipient.
 *
 * @param seal - The signed kind 13 seal event
 * @param recipientPubkeyHex - Recipient's public key (hex)
 * @returns The signed kind 1059 gift wrap event
 */
export function createGiftWrap(
  seal: NostrEvent,
  recipientPubkeyHex: string,
): NostrEvent {
  const randomKey = generateSecretKey();
  const randomPubkey = getPublicKey(randomKey);
  const randomKeyHex = bytesToHex(randomKey);

  const encryptedSeal = nip44Encrypt(seal, randomKeyHex, recipientPubkeyHex);

  const wrapTemplate: EventTemplate = {
    kind: 1059,
    content: encryptedSeal,
    tags: [['p', recipientPubkeyHex]],
    created_at: Math.floor(Date.now() / 1000),
    pubkey: randomPubkey,
  };

  return finalizeEvent(wrapTemplate, randomKey) as NostrEvent;
}

/**
 * Unwrap a gift wrap event to reveal the seal.
 *
 * Requires the recipient's private key.
 *
 * @param giftWrap - The kind 1059 gift wrap event
 * @param recipientPrivkeyHex - Recipient's private key (hex)
 * @returns The unwrapped kind 13 seal event
 */
export function unwrapGiftWrap(
  giftWrap: NostrEvent,
  recipientPrivkeyHex: string,
): NostrEvent {
  // Decrypt the content to get the seal
  const seal = nip44Decrypt<NostrEvent>(
    giftWrap.content,
    recipientPrivkeyHex,
    giftWrap.pubkey,
  );

  return seal;
}

/**
 * Unseal a seal event to reveal the rumor.
 *
 * @param seal - The kind 13 seal event
 * @param recipientPrivkeyHex - Recipient's private key (hex)
 * @returns The unwrapped rumor (unsigned event)
 */
export function unsealRumor(
  seal: NostrEvent,
  recipientPrivkeyHex: string,
): EventTemplate {
  const rumor = nip44Decrypt<EventTemplate>(
    seal.content,
    recipientPrivkeyHex,
    seal.pubkey,
  );

  return rumor;
}

/**
 * Full unwrap: gift wrap → seal → rumor.
 *
 * @param giftWrap - The kind 1059 gift wrap event
 * @param recipientPrivkeyHex - Recipient's private key (hex)
 * @returns The unwrapped rumor
 */
export function unwrapMessage(
  giftWrap: NostrEvent,
  recipientPrivkeyHex: string,
): { rumor: EventTemplate; senderPubkey: string } {
  const seal = unwrapGiftWrap(giftWrap, recipientPrivkeyHex);
  const rumor = unsealRumor(seal, recipientPrivkeyHex);
  return { rumor, senderPubkey: seal.pubkey };
}
